import { Component } from "react";
import RadarGraph from "./RadarGraph";
import BarGraph from "./BarGraph";
import "./PageCharts.css";
import "./EditPageCharts.css";
import messagesData from "../lang/preferences";
import HelpModal from "./HelpModal";
import { getTopExtremeTraitsUpperThird } from "../utils/traitsOrderMedian";
import ScopeReportIntroPage from "./ScopeReportIntroPageNoEdit";
import getThirdPosition from "../utils/getThirdPosition";
import { injectIntl } from "react-intl";
import ReportCoverPage from "./ReportCoverPage";
import ppeFullLogo from '../ressources/logo-with-text-green.svg'; 
import AllPersonalityTraitsPages from "./AllPersonalityTraitsPagesNoEdit";
import CopingDefinitionsPages from "./CopingDefinitionsPages";
import CopingDefinitionsPages2 from "./CopingDefinitionsPages2";
import EmotionalityDefinitionsPage from "./EmotionalityDefinitionsPage";
import WorkplaceValuesPages from "./WorkplaceValuesPages";
import ValueHierarchyPages from "./ValueHierarchyPages";
import { FormattedMessage } from "react-intl";

const PAGE_NUMBERS = [3, 7, 10, 12, 13, 17];

function SafeFormattedMessage({ msgKey }) {
  const msg = messagesData[msgKey];
  const valid = msg && typeof msg.id === "string" && msg.id.length > 0;
  if (valid) {
    return <FormattedMessage {...msg} />;
  }
  // Always return a React element, never a raw string
  return <span>{msgKey || ""}</span>;
}

function getTraitPerentile(trait, pageNumber = 0) {
  let traitKey = trait.en === "Self assurance" ? "Selfassurance" : trait.en;
  traitKey = traitKey.toUpperCase().replace(/ /g, "_");

  const label = trait.extremeLabel === "lowerThird" ? "LOW" : "HIGH";
  const percentile = trait.extremeLabel === "lowerThird" ? "25" : "100";

  if(pageNumber === 1 && trait.extremeLabel === "lowerThird") {
    trait.extremeLabel = "upperThird";
  }

  const prefix = (pageNumber === 6 || pageNumber === 5)
      ? `SCOPE_REPORT_${traitKey}`
      : `SCOPE_REPORT_${traitKey}_AXIS_${label}`;
  const percentilePrefix = `SCOPE_REPORT_${traitKey}_PERCENTILE_${percentile}`;
  const percentileTitle = `SCOPE_REPORT_PERCENTILE_${percentile}_TITLE`;

  if (pageNumber === 6 || pageNumber === 5) {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentileTitle}`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      collaboration: `${percentilePrefix}_COLLABORATION`,
      management: `${percentilePrefix}_MANAGEMENT`,
      recognition: `${percentilePrefix}_RECOGNITION`,
      teamKnow: `${percentilePrefix}_TEAM_KNOW`,
      teamKnowLabel: `SCOPE_REPORT_TEAM_KNOW_LABEL`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  } else {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentilePrefix}_TITLE`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      optimize4me: `SCOPE_REPORT_OPTIMIZE_FOR_ME_LABEL`,
      leversMotivation: `SCOPE_REPORT_LEVERS_OF_MOTIVATION_LABEL`,
      optimize: `${percentilePrefix}_OPTIMIZE`,
      motivation: `${percentilePrefix}_MOTIVATION`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  }
}

function renderAllTraitDetailsList({
  graph,
  index,
  filterIdx = 2,
  liClassName = "list-counter-marker",
  pageNumber = 1,
}) {
  const allTraits = graph?.dataTitles || [];
  return allTraits.flatMap((trait, idx) => {
    if (!trait || idx !== index) return [];

    trait = typeof trait === "string"
      ? trait.replace(/^SCOPE_REPORT_/, "") // Remove any existing SCOPE_REPORT_ prefix
      : trait;

    trait = typeof trait === "string"
      ? trait.replace("SCOPEREPORT_", "")
      : trait;

    trait = typeof trait === "string"
      ? trait.replace("EXCITEMENTSEEK", "EXCITEMENT_SEEKING")
      : trait;

    trait = typeof trait === "string"
      ? trait.replace("ENJOYWORK", "ENJOY_WORK")
      : trait;
    
        trait = typeof trait === "string"
      ? trait.replace("SECURITY", "JOB_SECURITY")
      : trait;

      trait = typeof trait === "string"
      ? trait.replace("INTELLECTUALSTIMULATION", "INTELLECTUAL_STIMULATION")
      : trait;
    
    trait = typeof trait === "string"
      ? trait.replace("TASKVARIABILITY", "TASK_VARIABILITY")
      : trait;
    trait = typeof trait === "string"
      ? trait.replace("HONOR", "INTEGRITY")
      : trait;
      
    if (typeof filterIdx === "function" ? !filterIdx(idx, trait) : idx !== filterIdx) return [];

    // For each trait, return two list items: one for LOWERTHIRD, one for UPPERTHIRD
    return ["LOWERTHIRD", "UPPERTHIRD"].map(extremeKey => {
      const prefix = `SCOPE_REPORT_${trait.toUpperCase().replace(/ /g, "_")}`;
      let percentilePrefix = `${prefix}_PERCENTILE_100`;
      let percentileTitle = `${prefix}_PERCENTILE_100_TITLE`;
      let getDetails = `SCOPE_REPORT_${trait.toUpperCase().replace(/ /g, "_")}_AXIS_HIGH_DESC`;
      let managerDetails = false;

      if (extremeKey === "LOWERTHIRD") {
        percentilePrefix = `${prefix}_PERCENTILE_25`;
        percentileTitle = `${prefix}_PERCENTILE_25_TITLE`;
        getDetails = `SCOPE_REPORT_${trait.toUpperCase().replace(/ /g, "_")}_AXIS_LOW_DESC`;
      }

      if (pageNumber === 5 || pageNumber === 6) {
        const traitKey = trait.toUpperCase().replace(/ /g, "_");
        if (extremeKey === "LOWERTHIRD") {
          getDetails = `SCOPE_REPORT_${traitKey}_DESC_LOW`;
          managerDetails = `SCOPE_REPORT_${traitKey}_MANAGER_DESC_LOW`;
        } else {
          getDetails = `SCOPE_REPORT_${traitKey}_DESC`;
          managerDetails = `SCOPE_REPORT_${traitKey}_MANAGER_DESC`;
        }
      }

      let traitDetails;
      if (pageNumber === 6 || pageNumber === 5) {
        traitDetails = {
          title: `${prefix}`,
          description: `${prefix}_DESC`,
          percentile: `${percentileTitle}`,
          strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
          strengths1: `${percentilePrefix}_STRENGTHS_1`,
          strengths2: `${percentilePrefix}_STRENGTHS_2`,
          strengths3: `${percentilePrefix}_STRENGTHS_3`,
          developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
          development1: `${percentilePrefix}_DEVELOPMENT_1`,
          development2: `${percentilePrefix}_DEVELOPMENT_2`,
          development3: `${percentilePrefix}_DEVELOPMENT_3`,
          teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
          collaboration: `${percentilePrefix}_COLLABORATION`,
          management: `${percentilePrefix}_MANAGEMENT`,
          recognition: `${percentilePrefix}_RECOGNITION`,
          teamKnow: `${percentilePrefix}_TEAM_KNOW`,
          teamKnowLabel: `SCOPE_REPORT_TEAM_KNOW_LABEL`,
          teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
        };
      } else {
        traitDetails = {
          title: `${prefix}${extremeKey === "LOWERTHIRD" ? "_AXIS_LOW" : "_AXIS_HIGH"}`,
          description: `${prefix}_DESC`,
          percentile: `${percentilePrefix}_TITLE`,
          strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
          strengths1: `${percentilePrefix}_STRENGTHS_1`,
          strengths2: `${percentilePrefix}_STRENGTHS_2`,
          strengths3: `${percentilePrefix}_STRENGTHS_3`,
          developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
          development1: `${percentilePrefix}_DEVELOPMENT_1`,
          development2: `${percentilePrefix}_DEVELOPMENT_2`,
          development3: `${percentilePrefix}_DEVELOPMENT_3`,
          optimize4me: `SCOPE_REPORT_OPTIMIZE_FOR_ME_LABEL`,
          leversMotivation: `SCOPE_REPORT_LEVERS_OF_MOTIVATION_LABEL`,
          optimize: `${percentilePrefix}_OPTIMIZE`,
          motivation: `${percentilePrefix}_MOTIVATION`,
          teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
          teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
        };
      }

      return (
        <li key={idx + "_" + extremeKey + "_traits"} className={liClassName}>
          <b className="font-18">
            <SafeFormattedMessage msgKey={traitDetails.title} />
          </b>{" "}
          (<SafeFormattedMessage msgKey={extremeKey} />)
          <span className="trait-details-box">
            <br />
            <SafeFormattedMessage msgKey={getDetails} />
            <br />
            {(pageNumber === 5 || pageNumber === 6) && managerDetails && (
              <div className="trait-details-list-box">
                <b>
                  <SafeFormattedMessage msgKey={'SCOPE_REPORT_MANAGER_SUMMARY_LABEL'} />
                </b>{" "}
                <SafeFormattedMessage msgKey={managerDetails} />
              </div>
            )}
            <span className="trait-details-list-box">
              <span>
                <span>
                  <SafeFormattedMessage msgKey={traitDetails.strengthsIntro} />
                </span>
                <ul className="trait-strengths-list" style={{ margin: 0, paddingLeft: 24 }}>
                  {[traitDetails.strengths1, traitDetails.strengths2, traitDetails.strengths3].map((key, idx) => {
                    if (!key || messagesData[key]?.id === undefined) return null;
                    return (
                      <li className="trait-strengths-list-item" key={idx}>
                        <SafeFormattedMessage msgKey={key} />
                      </li>
                    );
                  })}
                </ul>
              </span>
            </span>
            <span className="trait-details-list-box">
              <span>
                <span>
                  <SafeFormattedMessage msgKey={traitDetails.developmentIntro} />
                </span>
                <ul className="trait-development-list" style={{ margin: 0, paddingLeft: 24 }}>
                  {[traitDetails.development1, traitDetails.development2, traitDetails.development3].map((key, idx) => {
                    if (!key || messagesData[key]?.id === undefined) return null;
                    return (
                      <li className="trait-development-list-item" key={idx}>
                        <SafeFormattedMessage msgKey={key} />
                      </li>
                    );
                  })}
                </ul>
              </span>
            </span>
            {pageNumber !== 6 && (
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.optimize4me} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.optimize} />
                </span>
              </span>
            )}
            {pageNumber !== 6 && (
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.leversMotivation} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.motivation} />
                </span>
              </span>
            )}
            {pageNumber === 1 && (
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
                </span>
              </span>
            )}
            {pageNumber === 5 && (
              <>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.collaboration} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.management} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.recognition} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={traitDetails.teamKnowLabel} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.teamKnow} />
                  </span>
                </span>
              </>
            )}
            {pageNumber === 6 && (
              <>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.collaboration} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.management} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.recognition} />
                  </span>
                </span>
                <span className="trait-details-list-box">
                  <span>
                    <b>
                      <SafeFormattedMessage msgKey={"SCOPE_REPORT_ESSENTIAL_FOR_ME_LABEL"} />
                    </b>
                    <SafeFormattedMessage msgKey={traitDetails.teamKnow} />
                  </span>
                </span>
              </>
            )}
          </span>
        </li>
      );
    });
  });
}

function renderTraitDetailsList({
  data,
  type,
  filterIdx = 2,
  getTopTraits = getTopExtremeTraitsUpperThird,
  liClassName = "list-counter-marker",
  pageNumber = 1,
  getTraitPerentile,
}) {
  const topTraits = getTopTraits(data, type);
  // Convert any 'middleThird' labels to lower/upper depending on which half
  // of the middle interval the user's score is in. If in lower half -> lowerThird.
  try {
    const getUserScore = trait => typeof trait.user === 'number' ? trait.user : parseFloat(trait.user);
    topTraits.forEach(trait => {
      if (!trait || trait.extremeLabel !== 'middleThird') return;
      const userScore = getUserScore(trait);
      let getMiddleUpper, getMiddleBottom;
      if (type === 'bar') {
        const mt = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
        const lt = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
        getMiddleUpper = mt + lt;
        getMiddleBottom = lt;
      } else {
        getMiddleUpper = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
        getMiddleBottom = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
      }
      const middleCenter = (getMiddleUpper + getMiddleBottom) / 2;
      if (!isNaN(userScore) && userScore <= middleCenter) {
        trait.extremeLabel = 'lowerThird';
      } else {
        trait.extremeLabel = 'upperThird';
      }
    });
  } catch (e) {
    console.warn('Error determining middleThird half:', e);
  }
  return topTraits.map((trait, idx) => {
    if (typeof filterIdx === "function" ? !filterIdx(idx, trait) : idx !== filterIdx) return null;
    const traitDetails = getTraitPerentile(trait, pageNumber);
    const extremeKey = trait.extremeLabel ? trait.extremeLabel.toUpperCase() : "";
    let getDetails = traitDetails.description;
    let managerDetails = false;

    if (pageNumber === 6) {
      if (extremeKey === "LOWERTHIRD") {
        getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
        managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
      } else {
        getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
        managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
      }
    }
    if (pageNumber === 5) {
      if (extremeKey === "LOWERTHIRD") {
        getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
        managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
      } else {
        getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
        managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
      }
    }

    return (
      <li key={idx + "_traits"} className={liClassName}>
        <b className="font-18">
          <SafeFormattedMessage msgKey={traitDetails.title} />
        </b>{" "}
        (<SafeFormattedMessage msgKey={extremeKey} />)
        <span className="trait-details-box">
          <br />
          <SafeFormattedMessage msgKey={getDetails} />
          <br />
          {(pageNumber === 5 || pageNumber === 6) && managerDetails && (
            <div className="trait-details-list-box">
              <b>
                <SafeFormattedMessage msgKey={'SCOPE_REPORT_MANAGER_SUMMARY_LABEL'} />
              </b>{" "}
              <SafeFormattedMessage msgKey={managerDetails} />
            </div>
          )}
          <span className="trait-details-list-box">
            <span>
              <span>
                <SafeFormattedMessage msgKey={traitDetails.strengthsIntro} />
              </span>
              <ul className="trait-strengths-list" style={{ margin: 0, paddingLeft: 24 }}>
                {[traitDetails.strengths1, traitDetails.strengths2, traitDetails.strengths3].map((key, idx) => {
                  if (!key || messagesData[key]?.id === undefined) return null;
                  return (
                    <li className="trait-strengths-list-item" key={idx}>
                      <SafeFormattedMessage msgKey={key} />
                    </li>
                  );
                })}
              </ul>
            </span>
          </span>
          <span className="trait-details-list-box">
            <span>
              <span>
                <SafeFormattedMessage msgKey={traitDetails.developmentIntro} />
              </span>
              <ul className="trait-development-list" style={{ margin: 0, paddingLeft: 24 }}>
                {[traitDetails.development1, traitDetails.development2, traitDetails.development3].map((key, idx) => {
                  if (!key || messagesData[key]?.id === undefined) return null;
                  return (
                    <li className="trait-development-list-item" key={idx}>
                      <SafeFormattedMessage msgKey={key} />
                    </li>
                  );
                })}
              </ul>
            </span>
          </span>
          {pageNumber !== 6 && (
            <span className="trait-details-list-box">
              <span>
                <b>
                  <SafeFormattedMessage msgKey={traitDetails.optimize4me} />
                </b>
                <SafeFormattedMessage msgKey={traitDetails.optimize} />
              </span>
            </span>
          )}
          {pageNumber !== 6 && (
            <span className="trait-details-list-box">
              <span>
                <b>
                  <SafeFormattedMessage msgKey={traitDetails.leversMotivation} />
                </b>
                <SafeFormattedMessage msgKey={traitDetails.motivation} />
              </span>
            </span>
          )}
          {pageNumber === 1 && (
            <span className="trait-details-list-box">
              <span>
                <b>
                  <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                </b>
                <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
              </span>
            </span>
          )}
          {pageNumber === 5 && (
            <>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.collaboration} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.management} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.recognition} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.teamKnowLabel} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.teamKnow} />
                </span>
              </span>
            </>
          )}
          {pageNumber === 6 && (
            <>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={traitDetails.teamAdviceLabel} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.teamAdvice} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.collaboration} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.management} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.recognition} />
                </span>
              </span>
              <span className="trait-details-list-box">
                <span>
                  <b>
                    <SafeFormattedMessage msgKey={"SCOPE_REPORT_ESSENTIAL_FOR_ME_LABEL"} />
                  </b>
                  <SafeFormattedMessage msgKey={traitDetails.teamKnow} />
                </span>
              </span>
            </>
          )}
        </span>
      </li>
    );
  });
}

class AllEditPageCharts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      premium: true,
      loading: false,
      widthParentBar: "90%",
      heightParentBar: "50%",
      widthParentRadar: "90%",
      heightParentRadar: "50%",
      many: "top",
      graphs: [],
    };
  }

  updateMessage = (key, newValue) => {
    // If you want to allow editing, this should update messages in the parent,
    // not in local state. For now, do nothing or call a prop function.
    if (this.props.updateMessage) {
      this.props.updateMessage(key, newValue);
    }
  };

_displayGraph = (type, data, cp = false) => {
  switch (type) {
    case "radar":
      return (
        <div className="PageCharts-parentRadar">
          {data && data.length === 3 && (
            <div>
              <div className="action-orientation-box">
                <div className="action-orientation-box expressivity-box">
                  <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS" /></b>
                  <div className="expressivity-desc emotionality-desc">
                    <SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS_DESC" />
                  </div>
                </div>
              </div>
              <div className="action-orientation-box">
                <div className="action-orientation-box regulation-box">
                  <b><SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION" /></b>
                  <div className="emotionality-desc regulation-desc">
                    <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION_DESC" />
                  </div>
                </div>
              </div>
              <div className="action-orientation-box">
                <div className="action-orientation-box resilience-box">
                  <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE" /></b>
                  <div className="emotionality-desc resilience-desc">
                    <SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE_DESC" />
                  </div>
                </div>
              </div>
            </div>
          )}
          <RadarGraph data={data} />
        </div>
      );
    case "bar":
      return (
        <div className={`PageCharts-parentBar${cp ? '-horizontal' : ''}`}>
          {!cp && data?.length === 9 && data[8]?.fr === "Style cognitif" && (
            <div className="scope-personality-table-container">
              <table className="scope-table-fullwidth scope-personality-table-spacing">
                <tbody>
                  {/* Extraversion */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Excitement Seeking */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Impulsivity */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Pessimism */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Self-Assurance */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Dominance */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col  col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Aggressiveness */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                  {/* Conscientiousness */}
                  <tr>
                    <td className="scope-table-col">
                      <div className="scope-table-title-left">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW_DESC" />
                    </td>
                    <td className="col-middle"></td>
                    <td className="scope-table-col col-right">
                      <div className="scope-table-title-right">
                        <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH" />
                      </div>
                      <br />
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH_DESC" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <BarGraph data={data} cp={cp} />
        </div>
      );
    default:
      return null;
  }
};

  componentDidMount() {
    this.setState({
      graphs: this.props.dataForGraphs,
      code: this.props.code,
    });
  }

  handleReload = () => {
  window.location.reload();
};

render() {
  const { messages, intl } = this.props;
  const locale = intl.locale;

  return (
    <div className="PageCharts">
      {this.state.graphs.map((graph, key) => {
        const p = key + 1;
        const keyPage = PAGE_NUMBERS[key] || 15;

        return (
          <div key={key}>
            {/* Cover page */}
            {p === 1 && (
              <ReportCoverPage
                SafeFormattedMessage={SafeFormattedMessage}
                messages={messages}
                code={this.state.code}
                datePassed={this.props.datePassed}
                timeTaken={this.props.timeTaken}
                locale={locale}
                dataForGraphs={this.props.dataForGraphs}
                location={this.props.location}
              />
            )}

            {/* Intro page */}
            {p === 1 && (
              <ScopeReportIntroPage
                messages={messages}
                SafeFormattedMessage={SafeFormattedMessage}
                email={this.state.code}
                datePassed={this.props.datePassed}
                pageNumber={p + 1}
                pageNumbers={PAGE_NUMBERS}
                updateMessage={this.updateMessage}
                editble={true}
                locale={locale}
                logo={ppeFullLogo}
              />
            )}

            {/* Help modal */}
            <HelpModal dataTitles={graph.dataTitles} dataDescriptions={graph.dataDescriptions} />

            {/* Main chart */}
            <div className="PageCharts-chart">
              <div className="header-logo-box">
                <img
                  alt="ppe-logo"
                  className="header-logo-img"
                  src={ppeFullLogo}
                />
              </div>
              <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                <div className="PageCharts-head">
                  <div className="PageCharts-title">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                    </h1>
                    <p>
                      <SafeFormattedMessage msgKey={graph.description} />
                    </p>
                  </div>
                </div>
                {graph.type !== "radar" && (
                  <div className="PageCharts-svg PageCharts-svg-left">
                    {this._displayGraph(graph.type, graph.data)}
                  </div>
                )}
                {graph.type === "radar" && (
                  <div className="PageCharts-svg">{this._displayGraph(graph.type, graph.data)}</div>
                )}
              </div>
              <div className="pdf-footer">
                <SafeFormattedMessage msgKey="PAGE" /> {keyPage}{" "}
                <div className="pdf-footer-separator">
                  <b>
                    <SafeFormattedMessage msgKey="CANDIDATE" />
                  </b>
                </div>{" "}
                {this.state.code}
                <div className="pdf-footer-separator">
                  <b>
                    <SafeFormattedMessage msgKey="TEST_DATE" />
                  </b>{" "}
                  {this.props.datePassed}
                </div>
              </div>
            </div>

            {/* Definitions and trait pages */}
            {p === 1 && (
              <>
                <AllPersonalityTraitsPages
                  messagesData={messagesData}
                  locale={locale}
                  messages={messages}
                  updateMessage={this.updateMessage}
                  graph={graph}
                  code={this.state.code}
                  datePassed={this.props.datePassed}
                  keyPage={keyPage}
                  pageNumber={p}
                  getTraitPerentile={getTraitPerentile}
                  renderTraitDetailsList={renderTraitDetailsList} 
                />
              </>
            )}

            {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                    <div className="PageCharts-head">
                      <div className="PageCharts-title">
                        <h1>
                          <SafeFormattedMessage
                          msgKey="SCOPEREPORT_CAPACITIYREASONING"
                        />
                        </h1>
                        <p>
                          <SafeFormattedMessage
                            msgKey="SCOPEREPORT_CAPACITIYREASONING_DESC"
                          />
                        </p>
                      </div>
                    </div>
                    {graph.type !== "radar" && (
                      <div className="PageCharts-svg PageCharts-svg-left">
                        {this._displayGraph(graph.type, graph.data, true)}
                      </div>
                    )}
                    {graph.type === "radar" && (
                      <div className="PageCharts-svg">
                        {this._displayGraph(graph.type, graph.data)}
                      </div>
                    )}
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage
                      msgKey="PAGE"
                    />{" "}
                    {keyPage + 3}
                    <div className="pdf-footer-separator">
                      <b>
                        <SafeFormattedMessage
                          msgKey="CANDIDATE"
                        />
                      </b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b>
                        <SafeFormattedMessage
                          msgKey="TEST_DATE"
                        />
                      </b>{" "}
                      {this.props.datePassed}
                    </div>
                  </div>
                </div>
              )}

            {/* Coping Definitions and trait pages */}
            {p === 2 && (
              <>
                <CopingDefinitionsPages
                  graph={graph}
                  code={this.state.code}
                  datePassed={this.props.datePassed}
                  keyPage={keyPage}
                  getThirdPosition={getThirdPosition}
                  SafeFormattedMessage={SafeFormattedMessage}
                />
                 <CopingDefinitionsPages2
                  graph={graph}
                  code={this.state.code}
                  datePassed={this.props.datePassed}
                  keyPage={keyPage}
                  getThirdPosition={getThirdPosition}
                  SafeFormattedMessage={SafeFormattedMessage}
                />
              </>
            )}

            {/* Emotionality Definitions page */}
            {p === 3 && (
              <EmotionalityDefinitionsPage
                messagesData={messagesData}
                locale={locale}
                messages={messages}
                updateMessage={this.updateMessage}
                graph={graph}
                code={this.state.code}
                datePassed={this.props.datePassed}
                keyPage={keyPage}
              />
            )}

            {p === 5 && (
              <WorkplaceValuesPages
                messagesData={messagesData}
                locale={locale}
                messages={messages}
                updateMessage={this.updateMessage}
                graph={graph}
                code={this.state.code}
                datePassed={this.props.datePassed}
                keyPage={keyPage}
                pageNumber={p}
              />
            )}

            {/* Workplace Values - one trait per page, loop over 6 traits */}
            {p === 5 && (
              <>
                {[0, 1, 2, 3, 4, 5].map((traitIdx) => (
                  <div className="PageCharts-chart" key={traitIdx}>
                    <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                    </div>
                    <div className="report-alt-page">
                      <h1>
                        <SafeFormattedMessage
                          msgKey={graph.title}
                        />
                        <span className="inline font-13">
                          {" "}
                          - <SafeFormattedMessage
                            msgKey="SCOPE_REPORT_TOP_THREE"
                          />
                        </span>
                      </h1>
                      <ol className="list-counter"  start={traitIdx * 2 + 1}>
                        {renderAllTraitDetailsList({
                          graph,
                          index: traitIdx,      // loop index for trait
                          filterIdx: traitIdx,  // loop index for trait
                          pageNumber: p,
                          messages,
                          updateMessage: this.updateMessage,
                          locale,
                          getTraitPerentile
                        })}
                      </ol>
                    </div>
                    <div className="pdf-footer">
                      <SafeFormattedMessage
                        msgKey="PAGE"
                      />{" "}
                      {keyPage + traitIdx} {/* Increment page number */}
                      <div className="pdf-footer-separator">
                        <b>
                          <SafeFormattedMessage
                            msgKey="CANDIDATE"
                          />
                        </b>
                      </div>{" "}
                      {this.state.code}
                      <div className="pdf-footer-separator">
                        <b>
                          <SafeFormattedMessage
                           msgKey="TEST_DATE"
                          />
                        </b>{" "}
                        {this.props.datePassed}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {p === 6 && (
              <ValueHierarchyPages
                messagesData={messagesData}
                locale={locale}
                messages={messages}
                updateMessage={this.updateMessage}
                graph={graph}
                code={this.state.code}
                datePassed={this.props.datePassed}
                keyPage={keyPage}
                pageNumber={p}
              />
            )}

            {/* Value hierarchy - one trait per page, loop over 6 traits */}
            {p === 6 && (
              <>
                {[0, 1, 2, 3, 4, 5].map((traitIdx) => (
                  <div className="PageCharts-chart" key={traitIdx}>
                    <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                    </div>
                    <div className="report-alt-page">
                      <h1>
                        <SafeFormattedMessage
                          msgKey={graph.title}
                        />
                        <span className="inline font-16">
                          {" "}
                          - <SafeFormattedMessage
                            msgKey="SCOPE_REPORT_TOP_THREE"
                          />
                        </span>
                      </h1>
                      <ol className="list-counter" start={traitIdx * 2 + 1}>
                        {renderAllTraitDetailsList({
                          graph,
                          index: traitIdx,      // loop index for trait
                          filterIdx: traitIdx,  // loop index for trait
                          pageNumber: p,
                          messages,
                          updateMessage: this.updateMessage,
                          locale,
                          getTraitPerentile
                        })}
                      </ol>
                    </div>
                    <div className="pdf-footer">
                      <SafeFormattedMessage
                        msgKey="PAGE"
                      />{" "}
                      {keyPage + traitIdx} {/* Increment page number */}
                      <div className="pdf-footer-separator">
                        <b>
                          <SafeFormattedMessage
                            msgKey="CANDIDATE"
                          />
                        </b>
                      </div>{" "}
                      {this.state.code}
                      <div className="pdf-footer-separator">
                        <b>
                          <SafeFormattedMessage
                            msgKey="TEST_DATE"
                          />
                        </b>{" "}
                        {this.props.datePassed}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Personal Notes Page - LAST PAGE */}
            {p === 6 && (
              <div className="PageCharts-chart">
                <div className="header-logo-box">
                  <img
                    alt="ppe-logo"
                    className="header-logo-img"
                    src={ppeFullLogo}
                  />
                </div>
                <div className="header-logo-box">
                  <img
                    alt="ppe-logo"
                    className="header-logo-img"
                    src={ppeFullLogo}
                  />
                </div>
                <div className="report-alt-page">
                  <h1>
                    <SafeFormattedMessage
                      msgKey="SCOPE_REPORT_PERSONAL_NOTES_TITLE"
                    />
                  </h1>
                  <div className="personal-notes-section">
                    <div className="personal-notes-block">
                      <b>
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS"
                        />
                      </b>
                      <div className="personal-notes-italic">
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_EXAMPLE"
                        />
                      </div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line personal-notes-line-space"></div>
                    </div>
                    <div className="personal-notes-block">
                      <b>
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS"
                        />
                      </b>
                      <div className="personal-notes-italic">
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS_EXAMPLE"
                        />
                      </div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line personal-notes-line-space"></div>
                    </div>
                    <div className="personal-notes-block">
                      <b>
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT"
                        />
                      </b>
                      <div className="personal-notes-italic">
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT_EXAMPLE"
                        />
                      </div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line personal-notes-line-space"></div>
                    </div>
                    <div className="personal-notes-block">
                      <b>
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES"
                        />
                      </b>
                      <div className="personal-notes-italic">
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES_EXAMPLE"
                        />
                      </div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line personal-notes-line-space"></div>
                    </div>
                    <div className="personal-notes-block">
                      <b>
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION"
                        />
                      </b>
                      <div className="personal-notes-italic">
                        <SafeFormattedMessage
                          msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION_EXAMPLE"
                        />
                      </div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                      <div className="personal-notes-line"></div>
                    </div>
                  </div>
                </div>
                <div className="pdf-footer">
                  <SafeFormattedMessage
                    msgKey="PAGE"
                  />{" "}
                  {keyPage + 4}
                  <div className="pdf-footer-separator">
                    <b>
                      <SafeFormattedMessage
                        msgKey="CANDIDATE"
                      />
                    </b>
                  </div>{" "}
                  {this.state.code}
                  <div className="pdf-footer-separator">
                    <b>
                      <SafeFormattedMessage
                        msgKey="TEST_DATE"
                      />
                    </b>{" "}
                    {this.props.datePassed}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {this.state.showReloadModal && (
        <div className="modal-overlay reload-button" onClick={this.handleReload}>
          <div className="modal-content">
            <p>Translation updated! Please reload the page to see changes.</p>
            <button onClick={this.handleReload}>Reload Page</button>
          </div>
        </div>
      )}
    </div>
  );
}
}

export default injectIntl(AllEditPageCharts);